import { useId } from "react";
import type { ReactElement } from "react";
import type { LayerVisibility } from "../../state/layerState";
import type { InspectorPresentation } from "./BaseLayersCard";
import { InspectorRow } from "./InspectorRow";
import { SubsectionHeader } from "./SubsectionHeader";

export const TERRAIN_GENERAL_KEYS = [
  "terrain",
  "contours",
  "contourLabels",
  "buildabilityHeatmap"
] as const;

export const TERRAIN_SLOPE_KEYS = [
  "slopeHeatmap",
  "slopeArrows",
  "drainageFlow",
  "elevationMarkers"
] as const;

export type TerrainVisibilityKey =
  | (typeof TERRAIN_GENERAL_KEYS)[number]
  | (typeof TERRAIN_SLOPE_KEYS)[number];

const GENERAL_LABELS: Record<(typeof TERRAIN_GENERAL_KEYS)[number], string> = {
  terrain: "Terrain surface",
  contours: "Contours",
  contourLabels: "Contour labels",
  buildabilityHeatmap: "Buildability heatmap"
};

const SLOPE_LABELS: Record<(typeof TERRAIN_SLOPE_KEYS)[number], string> = {
  slopeHeatmap: "Heatmap",
  slopeArrows: "Direction arrows",
  drainageFlow: "Drainage / low points",
  elevationMarkers: "High / low markers"
};

export interface TerrainLayersCardProps {
  visibility: LayerVisibility;
  onToggle: (key: TerrainVisibilityKey, value: boolean) => void;
  presentation?: InspectorPresentation;
  /** Visibility keys whose value differs from the viewer default (accent dot). */
  modifiedKeys?: ReadonlySet<string>;
}

export function TerrainLayersCard({
  visibility,
  onToggle,
  modifiedKeys
}: TerrainLayersCardProps): ReactElement {
  const reactId = useId();

  return (
    <div>
      <SubsectionHeader label="General" first />
      {TERRAIN_GENERAL_KEYS.map((key) => (
        <InspectorRow
          key={key}
          label={GENERAL_LABELS[key]}
          labelWeight="normal"
          checkboxId={`${reactId}-gen-${key}`}
          checked={visibility[key]}
          modified={modifiedKeys?.has(key) === true}
          onCheckedChange={(v) => {
            onToggle(key, v);
          }}
        />
      ))}
      <SubsectionHeader label="Slope" />
      {TERRAIN_SLOPE_KEYS.map((key) => (
        <InspectorRow
          key={key}
          label={SLOPE_LABELS[key]}
          labelWeight="normal"
          checkboxId={`${reactId}-slope-${key}`}
          checked={visibility[key]}
          modified={modifiedKeys?.has(key) === true}
          onCheckedChange={(v) => {
            onToggle(key, v);
          }}
        />
      ))}
    </div>
  );
}
